// Vue Offres — port de rec8-offres.jsx (cartes par candidature, détail
// pleine page rec7) + constructeur rec-offre.jsx + réponse candidat.
// Règles préservées: l'offre ne se construit que depuis un finaliste,
// chaque version repasse par directeur → owner, les montants sont des
// champs restreints (RecSal), le PDF se joint à l'envoi.
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ENTITIES, REC_OFFER_AVANTAGES, REC_OFFER_DEADLINES, REC_OFFER_ESSAI } from '@pointage/shared';
import {
  recOfferPdfUrl,
  type RecApplicationDTO, type RecOfferDTO, type RecOfferInput, type RecRequisitionDTO, type RecRoleDTO,
} from '../../api/hooks.js';
import {
  RecAv, RecBand, RecChain, RecEnt, RecSal, recContrat, recFmtDate, recFmtMoney,
  recOfferDaysLeft, recOfferOf, recOfferStOf,
} from './common.js';
import { recFmtDT, recJuryAvg } from './EvalSections.js';
import { useRecSt } from './V2Sections.js';
import { BarPill } from './V2Ui.js';

const entOf = (id: string) => ENTITIES.find((e) => e.id === id);

// ── petites briques ─────────────────────────────────────────

/** Chaîne compacte d'une carte (r6-chainrow). */
function ChainRow({ o }: { o: RecOfferDTO }) {
  return (
    <div className="r6-chainrow">
      {o.approvals.map((a, j) => (
        <span key={a.step} style={{ display: 'contents' }}>
          {j > 0 && <span className={`r6-link${o.approvals[j - 1]!.status === 'validee' ? ' ok' : ''}`}></span>}
          <span className={`r6-cs ${a.status === 'validee' ? 'ok' : a.status === 'refusee' ? 'ko' : a.status === 'courante' ? 'now' : ''}`}>
            <span className="p">{a.status === 'validee' ? '✓' : a.status === 'refusee' ? '✕' : '●'}</span>
            {a.roleLabel.split(' ')[0]}
          </span>
        </span>
      ))}
      {!o.approvals.length && <span className="r6-cs"><span className="p">●</span>Brouillon — pas encore soumis</span>}
    </div>
  );
}

/** Diff entre deux versions (rec-diff du proto). */
export function OfferDiff({ a, b, canSal }: { a: RecOfferDTO; b: RecOfferDTO; canSal: boolean }) {
  const rows: Array<[string, string, string, boolean]> = [
    ['Salaire brut', canSal ? recFmtMoney(a.salaire ?? 0) : '🔒', canSal ? recFmtMoney(b.salaire ?? 0) : '🔒', a.salaire !== b.salaire],
    ['Primes', a.primes ?? '—', b.primes ?? '—', (a.primes ?? '') !== (b.primes ?? '')],
    ['Avantages', a.avantages.join(' · ') || '—', b.avantages.join(' · ') || '—', a.avantages.join() !== b.avantages.join()],
    ['Date de début', recFmtDate(a.startDate), recFmtDate(b.startDate), a.startDate !== b.startDate],
    ['Période d’essai', a.essai, b.essai, a.essai !== b.essai],
  ];
  return (
    <div className="rec-diff">
      <div className="dr"><span className="k"></span><span className="k">Version {a.version}</span><span className="k">Version {b.version}</span></div>
      {rows.map(([k, x, y, chg]) => (
        <div key={k} className={`dr${chg ? ' chg' : ''}`}>
          <span className="k">{k}</span><span className={chg ? 'old' : ''}>{x}</span><span className={chg ? 'new' : ''}>{y}</span>
        </div>
      ))}
    </div>
  );
}

/** Aperçu PDF (rec-pdf) — miroir fidèle du document généré. */
function PdfPreview({ o, app, req, canSal }: { o: RecOfferDTO; app: RecApplicationDTO; req: RecRequisitionDTO; canSal: boolean }) {
  const ent = entOf(req.entityId);
  const c = app.candidate;
  return (
    <div className="rec-pdf">
      <div className="bar"><i></i><i></i><i></i>offre-{c.lastName.toLowerCase()}-v{o.version}.pdf</div>
      <div className="pg">
        <h5>Proposition d’embauche</h5>
        <p style={{ margin: 0 }}>
          {ent?.name} propose à <span className="var">{c.firstName} {c.lastName}</span> le poste de{' '}
          <span className="var">{req.roleLabel}</span> en <span className="var">{recContrat(req)}</span>, à compter du{' '}
          <span className="var">{recFmtDate(o.startDate)}</span>.
        </p>
        <p style={{ margin: '8px 0 0' }}>
          Rémunération brute mensuelle : <span className="var">{canSal && o.salaire != null ? recFmtMoney(o.salaire) : '•••••'}</span>
          {o.primes ? ` · ${o.primes}` : ''}.<br />
          {o.avantages.length > 0 && <>Avantages : {o.avantages.join(', ')}. </>}Période d’essai : <span className="var">{o.essai}</span>.
        </p>
        <p style={{ margin: '8px 0 0', color: 'var(--ink4)', fontSize: 10 }}>
          Validité : {o.responseDeadline ? `jusqu’au ${recFmtDate(o.responseDeadline)}` : `${o.deadlineDays} jours à réception`} ·
          génération PDF → envoi → scan signé (pas d’e-signature en v1).
        </p>
      </div>
    </div>
  );
}

/** Fil de suivi — envois, relances, réponses, décisions de chaîne. */
function OfferFeed({ offers }: { offers: RecOfferDTO[] }) {
  const rows: Array<{ at: string; t: string; ic: string; tx: React.ReactNode }> = [];
  for (const v of offers) {
    for (const a of v.approvals) {
      if (a.decidedAt && (a.status === 'validee' || a.status === 'refusee')) {
        rows.push({
          at: `${a.decidedAt}T12:00:00`, t: recFmtDate(a.decidedAt), ic: a.status === 'validee' ? '✓' : '✕',
          tx: <span>v{v.version} {a.status === 'validee' ? 'validée' : 'refusée'} — <b>{a.roleLabel}</b>{a.decider ? ` (${a.decider})` : ''}{a.comment ? <> · « {a.comment} »</> : null}</span>,
        });
      }
    }
    if (v.sentAt) {
      rows.push({ at: v.sentAt, t: recFmtDT(v.sentAt), ic: '✉️', tx: <span>Offre <b>v{v.version}</b> envoyée — deadline <b>{v.responseDeadline ? recFmtDate(v.responseDeadline) : '—'}</b></span> });
    }
    v.relances.forEach((r, i) => {
      rows.push({ at: r, t: recFmtDT(r), ic: '🔔', tx: <span>Relance n°{i + 1} — v{v.version}, PDF re-joint</span> });
    });
    if (v.respondedAt && v.response) {
      rows.push({
        at: v.respondedAt, t: recFmtDT(v.respondedAt), ic: v.response === 'acceptee' ? '🎉' : v.response === 'contre' ? '↩' : '✕',
        tx: v.response === 'acceptee' ? <span><b>Offre v{v.version} acceptée</b> — candidat recruté</span>
          : v.response === 'contre' ? <span><b>Contre-proposition</b> sur v{v.version} — la version suivante re-entre la chaîne</span>
            : <span><b>Offre v{v.version} refusée</b> par le candidat</span>,
      });
    }
  }
  rows.sort((a, b) => a.at.localeCompare(b.at));
  if (!rows.length) return <div className="rec-emptybox">Rien encore — le suivi démarre à la soumission.</div>;
  return (
    <div className="rec-feed">
      {rows.map((r, i) => (
        <div key={i} className="rec-feedrow"><span className="t">{r.t}</span><span className="ic">{r.ic}</span><p>{r.tx}</p></div>
      ))}
    </div>
  );
}

// ── actions par statut (partagé détail + section panel) ─────
export interface OfferActions {
  onBuild: (appId: string, draft: RecOfferDTO | null) => void; // constructeur (v1, édition brouillon ou v suivante)
  onSubmit: (offerId: string) => void;
  onSend: (offerId: string) => void;
  onRemind: (offerId: string) => void;
  onResponse: (offerId: string) => void; // ouvre la modal réponse
}

export function OfferActionButtons({ app, o, canEdit, canSal, busy, act, compact }: {
  app: RecApplicationDTO; o: RecOfferDTO; canEdit: boolean; canSal: boolean; busy: boolean;
  act: OfferActions; compact?: boolean;
}) {
  if (!canEdit || !canSal) return null;
  const sm = compact ? ' sm' : '';
  return (
    <>
      {o.status === 'BROUILLON' && (
        <>
          <button className={`rec-ghost${sm}`} disabled={busy} onClick={() => act.onBuild(app.id, o)}>✏️ Reprendre le brouillon</button>
          <button className={`rec-go${sm}`} disabled={busy} onClick={() => act.onSubmit(o.id)}>Soumettre à validation →</button>
        </>
      )}
      {o.status === 'VALIDEE' && (
        <button className={`rec-go${sm}`} disabled={busy} onClick={() => act.onSend(o.id)}>✉️ Envoyer au candidat — PDF joint</button>
      )}
      {o.status === 'ENVOYEE' && (
        <>
          <button className={`rec-ghost${sm}`} disabled={busy} onClick={() => act.onRemind(o.id)}>🔔 Relancer</button>
          <button className={`rec-go${sm}`} disabled={busy} onClick={() => act.onResponse(o.id)}>Enregistrer la réponse…</button>
        </>
      )}
      {o.status === 'CONTRE' && (
        <button className={`rec-go${sm}`} disabled={busy} onClick={() => act.onBuild(app.id, null)}>Construire la v{o.version + 1} →</button>
      )}
    </>
  );
}

// ── détail pleine page (r7/r8) ──────────────────────────────
function OfferDetail({ app, req, role, dark, canEdit, canSal, busy, act, onClose, onOpenCandidate }: {
  app: RecApplicationDTO; req: RecRequisitionDTO; role: RecRoleDTO | undefined;
  dark: boolean; canEdit: boolean; canSal: boolean; busy: boolean;
  act: OfferActions; onClose: () => void; onOpenCandidate: (appId: string) => void;
}) {
  const o = recOfferOf(app)!;
  const st = recOfferStOf(o.status);
  const ent = entOf(req.entityId);
  const prev = app.offers.length > 1 ? app.offers[app.offers.length - 2]! : null;
  const grid = role?.templates.grid ?? [];
  const { avg } = recJuryAvg(app, grid);
  const subs = app.interviews.flatMap((i) => i.scorecards ?? (i.myScorecard ? [i.myScorecard] : []));
  const dl = recOfferDaysLeft(o);
  return createPortal(
    <div className={`recapp rec-portal${dark ? ' theme-dark' : ''}`}>
      <div className="rec-ov" onClick={onClose}></div>
      <div className="r3-panel r8-full" style={{ '--sc': st.c } as React.CSSProperties}>
        <div className="r7-head">
          <RecAv app={app} big />
          <div style={{ minWidth: 0 }}>
            <div className="r7-title">Offre — {app.candidate.firstName} {app.candidate.lastName}</div>
            <div className="r7-sub">{req.roleLabel} · {ent?.name} · {recContrat(req)}</div>
          </div>
          <span className="r6-st" style={{ '--stc': st.c, background: st.c } as React.CSSProperties}>{st.lbl}</span>
          <span className="rec-vchip">v{o.version}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="rec-ghost sm" onClick={() => onOpenCandidate(app.id)}>Fiche candidat</button>
            {canSal && <a className="rec-ghost sm" href={recOfferPdfUrl(o.id)} target="_blank" rel="noreferrer">📄 PDF</a>}
            <OfferActionButtons app={app} o={o} canEdit={canEdit} canSal={canSal} busy={busy} act={act} compact />
            <button className="rec-x" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="r7-body">
          <div className="r7-grid">
            <div className="r7-col">
              <div className="r7-amtcard">
                <div className="k">Salaire brut mensuel — version {o.version}</div>
                <div className="big">{canSal && o.salaire != null ? recFmtMoney(o.salaire) : <RecSal band={null} />}</div>
                {canSal && o.salaire != null && req.band && <RecBand band={req.band} val={o.salaire} />}
                {canSal && o.salaire != null && req.band && (o.salaire < req.band[0] || o.salaire > req.band[1]) && (
                  <div className="rec-outband"><span>⚠</span><span><b>Hors fourchette du rôle.</b> L’écart est visible dans la file de validation et compté dans « écart offre vs grille ».</span></div>
                )}
                <div className="r7-facts">
                  <div className="r7-fact"><div className="k">Contrat</div><div className="v">{recContrat(req)}</div></div>
                  <div className="r7-fact"><div className="k">Période d’essai</div><div className="v">{o.essai}</div></div>
                  <div className="r7-fact"><div className="k">Date de début</div><div className="v">{recFmtDate(o.startDate)}</div></div>
                  <div className="r7-fact"><div className="k">Primes</div><div className="v">{canSal ? o.primes ?? '—' : '🔒'}</div></div>
                  <div className="r7-fact" style={{ gridColumn: 'span 2' }}><div className="k">Avantages</div><div className="v">{o.avantages.join(' · ') || '—'}</div></div>
                  {o.responseDeadline && (
                    <div className="r7-fact"><div className="k">Deadline réponse</div><div className="v">{recFmtDate(o.responseDeadline)}{dl != null && <span className={`r6-dl ${dl <= 3 ? 'hot' : 'okk'}`} style={{ marginLeft: 8 }}>{dl >= 0 ? `J−${dl}` : 'dépassée'}</span>}</div></div>
                  )}
                </div>
                {o.refusalComment && o.status === 'BROUILLON' && (
                  <div className="rec-refusnote" style={{ marginTop: 12 }}>✕ Refusée en interne — « {o.refusalComment} ». Ajustez puis re-soumettez.</div>
                )}
              </div>
              {prev && (
                <div className="r7-sec">
                  <h4>↩ Négociation — v{prev.version} → v{o.version}</h4>
                  {prev.counterDetails && (
                    <div className="rec-consent" style={{ marginTop: 0, marginBottom: 10 }}>
                      Contre-proposition{prev.respondedAt ? ` du ${recFmtDT(prev.respondedAt)}` : ''} : <b>{canSal ? prev.counterDetails : 'détails restreints — RH & direction'}</b>
                    </div>
                  )}
                  <OfferDiff a={prev} b={o} canSal={canSal} />
                  <p style={{ margin: '10px 0 0', fontSize: 11.5, color: 'var(--ink3)', lineHeight: 1.5 }}>
                    Chaque nouvelle version repasse intégralement par la chaîne de validation — aucune modification silencieuse après validation.
                  </p>
                </div>
              )}
              <div className="r7-sec"><h4>📡 Suivi de l’offre</h4><OfferFeed offers={app.offers} /></div>
            </div>
            <div className="r7-col">
              <div className="r7-sec"><h4>✓ Chaîne de validation — v{o.version}</h4>
                {o.approvals.length ? <RecChain approvals={o.approvals} /> : <div className="rec-emptybox">Brouillon — la chaîne s’arme à la soumission.</div>}
              </div>
              <div className="r7-sec"><h4>★ Jury & scorecards</h4>
                {avg != null && (
                  <div className="rec-agg" style={{ marginTop: 0, marginBottom: 12 }}>
                    <span className="big">{avg}<i>/5</i></span>
                    <div className="sub">moyenne pondérée<br />{subs.length} scorecard{subs.length > 1 ? 's' : ''} soumise{subs.length > 1 ? 's' : ''}</div>
                  </div>
                )}
                <div className="r7-jury">
                  {subs.map((s) => (
                    <div key={s.id} className="r7-jr">{s.author}
                      <span className={`rec-vd ${s.verdict}`}>{s.verdict === 'oui' ? 'OUI' : s.verdict === 'reserve' ? 'RÉSERVE' : 'NON'}</span>
                    </div>
                  ))}
                  {!subs.length && <div style={{ fontSize: 12, color: 'var(--ink4)', fontWeight: 600 }}>Aucune scorecard.</div>}
                </div>
              </div>
              <div className="r7-sec"><h4>📄 Aperçu PDF — {ent?.name} · {recContrat(req)}</h4>
                <PdfPreview o={o} app={app} req={req} canSal={canSal} />
                {canSal && (
                  <a className="rec-ghost sm" style={{ marginTop: 10, display: 'inline-flex' }} href={recOfferPdfUrl(o.id)} target="_blank" rel="noreferrer">Télécharger le PDF ↗</a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── vue Offres (cartes r6) ──────────────────────────────────
type Filter = 'all' | 'brouillon' | 'validation' | 'aenvoyer' | 'envoyee' | 'nego' | 'close';
const FILTERS: Array<[Filter, string]> = [
  ['all', 'Toutes'], ['brouillon', 'Brouillons'], ['validation', 'En validation'],
  ['aenvoyer', 'À envoyer'], ['envoyee', 'Envoyées'], ['nego', 'Négociation'], ['close', 'Clôturées'],
];

function matches(app: RecApplicationDTO, f: Filter): boolean {
  const o = recOfferOf(app)!;
  switch (f) {
    case 'all': return true;
    case 'brouillon': return o.status === 'BROUILLON';
    case 'validation': return o.status === 'EN_ATTENTE';
    case 'aenvoyer': return o.status === 'VALIDEE';
    case 'envoyee': return o.status === 'ENVOYEE';
    case 'nego': return o.status === 'CONTRE' || app.offers.length > 1;
    case 'close': return o.status === 'ACCEPTEE' || o.status === 'REFUSEE';
  }
}

export function RecOffres({ apps, reqs, roles, dark, canEdit, canSal, busy, sel, onSel, act, onOpenCandidate }: {
  apps: RecApplicationDTO[];
  reqs: RecRequisitionDTO[];
  roles: RecRoleDTO[];
  dark: boolean; canEdit: boolean; canSal: boolean; busy: boolean;
  sel: string | null; // application id du détail ouvert
  onSel: (appId: string | null) => void;
  act: OfferActions;
  onOpenCandidate: (appId: string) => void;
}) {
  const [f, setF] = useState<Filter>('all');
  const reqOf = useMemo(() => new Map(reqs.map((r) => [r.id, r])), [reqs]);
  const rows = useMemo(
    () => apps.filter((a) => a.offers.length > 0 && reqOf.has(a.requisitionId)),
    [apps, reqOf],
  );
  const count = (k: Filter) => rows.filter((a) => matches(a, k)).length;
  const list = rows.filter((a) => matches(a, f));
  const hors = canSal
    ? rows.filter((a) => {
        const o = recOfferOf(a)!;
        const band = reqOf.get(a.requisitionId)?.band;
        return band && o.salaire != null && (o.salaire < band[0] || o.salaire > band[1]);
      }).length
    : null;

  const kpis: Array<[string | number, string, string]> = [
    [rows.length, 'Offres au total', 'var(--shf)'],
    [count('validation'), 'En attente de validation', 'var(--warn)'],
    [count('aenvoyer') + count('envoyee'), 'Validées & envoyées', 'var(--info)'],
    [count('nego'), 'En négociation', 'var(--pink)'],
    [hors ?? '🔒', 'Hors fourchette du rôle', hors ? 'var(--bad)' : 'var(--ink3)'],
  ];

  const selApp = sel ? rows.find((a) => a.id === sel) ?? null : null;
  const selReq = selApp ? reqOf.get(selApp.requisitionId) ?? null : null;

  return (
    <div>
      <div className="r6-kpis">
        {kpis.map(([v, l, c], i) => (
          <div key={i} className="r6-kpi" style={{ '--kc': c } as React.CSSProperties}><b>{v}</b><span>{l}</span></div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 14px', flexWrap: 'wrap' }}>
        <div className="rec-seg">
          {FILTERS.map(([k, l]) => (
            <button key={k} className={f === k ? 'on' : ''} onClick={() => setF(k)}>
              {l}{k !== 'all' && count(k) > 0 && <span className="bdg">{count(k)}</span>}
            </button>
          ))}
        </div>
        <span className="rec-hint" style={{ marginLeft: 'auto' }}>
          Aucune offre n’existe hors plateforme — chaque version repasse par directeur → owner
        </span>
      </div>
      <div className="r6-grid">
        {list.map((a, i) => {
          const o = recOfferOf(a)!;
          const st = recOfferStOf(o.status);
          const req = reqOf.get(a.requisitionId)!;
          const ent = entOf(req.entityId);
          const dl = recOfferDaysLeft(o);
          const subs = a.interviews.reduce((n, itw) => n + itw.scorecardCount, 0);
          return (
            <div key={a.id} className="r6-card r8-click" style={{ animationDelay: `${Math.min(i, 8) * 60}ms`, '--stc': st.c } as React.CSSProperties} onClick={() => onSel(a.id)}>
              <div className="r6-top">
                <RecAv app={a} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, letterSpacing: '-.01em' }}>{a.candidate.firstName} {a.candidate.lastName}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--ink3)', fontWeight: 600, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    {req.roleLabel}{ent && <RecEnt id={ent.id} name={ent.name} color={ent.color} />}
                  </div>
                </div>
                <span className="r6-st">{st.lbl}</span>
              </div>
              <div className="r6-mid">
                <div className="r6-amt">
                  {canSal && o.salaire != null ? <span className="m">{recFmtMoney(o.salaire)}</span> : <RecSal band={null} />}
                  <span className="rec-vchip">v{o.version}</span>
                  {a.offers.length > 1 && <span className="r4-chip" style={{ '--c4': '#2a6fdb' } as React.CSSProperties}>négo v1 → v{o.version}</span>}
                </div>
                {canSal && o.salaire != null && req.band && <RecBand band={req.band} val={o.salaire} />}
                <div className="r6-chips">
                  <span className="r6-chip">{recContrat(req)} · essai {o.essai}</span>
                  <span className="r6-chip">début {recFmtDate(o.startDate)}</span>
                  {o.sentAt && <span className="r6-chip">envoyée {recFmtDT(o.sentAt).split(' · ')[0]}</span>}
                  {dl != null && <span className={`r6-dl ${dl <= 3 ? 'hot' : 'okk'}`}>{dl >= 0 ? `J−${dl} deadline` : 'deadline dépassée'}</span>}
                  {o.relances.length > 0 && <span className="r6-chip">{o.relances.length} relance{o.relances.length > 1 ? 's' : ''}</span>}
                </div>
              </div>
              <ChainRow o={o} />
              <div className="r6-foot" onClick={(e) => e.stopPropagation()}>
                <button className="rec-go sm" disabled={busy} onClick={() => onSel(a.id)}>Détails & négociation</button>
                <OfferActionButtons app={a} o={o} canEdit={canEdit} canSal={canSal} busy={busy} act={act} compact />
                <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--ink4)', fontWeight: 700 }}>{subs} scorecard{subs > 1 ? 's' : ''} jury</span>
              </div>
            </div>
          );
        })}
        {!list.length && (
          <div className="rec-emptybox big" style={{ gridColumn: '1/-1' }}>
            ✉️ {f === 'all' ? 'Aucune offre — construisez-en une depuis un candidat finaliste (pipeline ou fiche candidat).' : 'Aucune offre dans ce filtre.'}
          </div>
        )}
      </div>
      {selApp && selReq && (
        <OfferDetail
          app={selApp} req={selReq} role={roles.find((r) => r.id === selReq.roleId)}
          dark={dark} canEdit={canEdit} canSal={canSal} busy={busy}
          act={act} onClose={() => onSel(null)} onOpenCandidate={onOpenCandidate}
        />
      )}
    </div>
  );
}

// ── constructeur (rec-offre RecBuilder) ─────────────────────
export function OfferBuilder({ app, req, role, draft, busy, onClose, onSave }: {
  app: RecApplicationDTO;
  req: RecRequisitionDTO;
  role: RecRoleDTO | undefined;
  draft: RecOfferDTO | null; // édition d'un brouillon existant
  busy: boolean;
  onClose: () => void;
  onSave: (input: RecOfferInput, submit: boolean) => void;
}) {
  const prev = recOfferOf(app);
  const base = draft ?? (prev?.status === 'CONTRE' ? prev : null);
  const band = req.band;
  const defStart = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  })();
  const [sal, setSal] = useState<number>(base?.salaire ?? (band ? Math.round((band[0] + band[1]) / 2 / 1000) * 1000 : 100_000));
  const [primes, setPrimes] = useState(base?.primes ?? '');
  const [av, setAv] = useState<string[]>(base?.avantages ?? ['Mutuelle famille']);
  const [essai, setEssai] = useState(base?.essai ?? '6 mois');
  const [start, setStart] = useState(base?.startDate ?? defStart);
  const [dl, setDl] = useState<number>(base?.deadlineDays ?? 10);
  const version = draft ? draft.version : (prev?.version ?? 0) + 1;
  const grid = role?.templates.grid ?? [];
  const { avg, n } = recJuryAvg(app, grid);
  const st = useRecSt();
  // v2 — prétentions structurées ; v1 gardait un texte libre dans answers.
  const pret = app.call?.pretAmount != null
    ? `${recFmtMoney(app.call.pretAmount)} ${app.call.pretType ?? ''}`.trim()
    : app.call?.answers.pret;
  const valid = sal > 0 && !!start && !!essai;
  const input = (): RecOfferInput => ({
    salaire: sal, ...(primes.trim() ? { primes: primes.trim() } : {}), avantages: av,
    essai, startDate: start, deadlineDays: dl,
  });
  return (
    <div className="rec-modal-ov" onClick={onClose}>
      <div className="rec-modal" style={{ width: 'min(760px,94vw)' }} onClick={(e) => e.stopPropagation()}>
        <div className="rec-modal-h">
          <h3>Constructeur d’offre — {app.candidate.firstName} {app.candidate.lastName}</h3>
          <span className="rec-vchip">v{version}</span>
          <button className="rec-x" onClick={onClose}>✕</button>
        </div>
        <div className="rec-modal-b">
          <div className="rec-consent" style={{ marginTop: 0, marginBottom: 14 }}>
            🔒 Modèle <b>{entOf(req.entityId)?.name} · {recContrat(req)}</b> pré-chargé depuis le rôle {req.roleLabel}. Montants restreints RH & direction.
          </div>
          {draft?.refusalComment && (
            <div className="rec-refusnote" style={{ marginBottom: 12 }}>✕ Refusée en interne — « {draft.refusalComment} »</div>
          )}
          {!draft && prev?.status === 'CONTRE' && prev.counterDetails && (
            <div className="rec-refusnote" style={{ marginBottom: 12, background: 'var(--infos)', color: 'var(--infoc)', borderColor: 'transparent' }}>
              ↩ Contre-proposition : « {prev.counterDetails} » — la v{version} re-entre la chaîne directeur → owner.
            </div>
          )}
          <div className="rec-f2" style={{ marginTop: 0 }}>
            <div className="rec-field"><label>Salaire brut mensuel (DZD)</label>
              <input type="number" step={1000} min={0} value={sal} onChange={(e) => setSal(Math.max(0, Math.round(+e.target.value || 0)))} />
            </div>
            <div className="rec-field"><label>Période d’essai</label>
              <select value={essai} onChange={(e) => setEssai(e.target.value)}>
                {REC_OFFER_ESSAI.map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
          </div>
          {band && <RecBand band={band} val={sal} />}
          {band && (sal < band[0] || sal > band[1]) && (
            <div className="rec-outband"><span>⚠</span><span><b>Hors fourchette du rôle.</b> L’écart sera visible dans la file de validation et compté dans les analytics « écart offre vs grille ».</span></div>
          )}
          <div className="rec-lbl" style={{ marginTop: 14 }}>Avantages</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {[...new Set([...REC_OFFER_AVANTAGES, ...av])].map((x) => (
              <button
                key={x} type="button"
                className={`rec-epill${av.includes(x) ? ' on' : ''}`}
                onClick={() => setAv(av.includes(x) ? av.filter((y) => y !== x) : [...av, x])}
              >{x}</button>
            ))}
          </div>
          <div className="rec-f2" style={{ marginTop: 14 }}>
            <div className="rec-field"><label>Primes (contractuelles)</label>
              <input value={primes} placeholder="ex. Prime performance trimestrielle" onChange={(e) => setPrimes(e.target.value)} />
            </div>
            <div className="rec-field"><label>Date de début</label>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
          </div>
          <div className="rec-f2">
            <div className="rec-field"><label>Deadline de réponse (relance en un clic après envoi)</label>
              <select value={dl} onChange={(e) => setDl(+e.target.value)}>
                {REC_OFFER_DEADLINES.map((x) => <option key={x} value={x}>{x} jours</option>)}
              </select>
            </div>
            <div></div>
          </div>
          <div className="rec-recap" style={{ marginTop: 14 }}>
            <div className="r"><span>Contexte de décision</span><b>{n} scorecard{n > 1 ? 's' : ''}{avg != null ? ` · moyenne ${avg}/5` : ''}</b></div>
            <div className="r"><span>Barre d’embauche</span><b><BarPill bar={app.gates.masked ? null : app.gates.bar} overridden={app.gates.overridden} st={st} /></b></div>
            <div className="r"><span>Prétentions (appel)</span><b>{pret || '—'}</b></div>
            <div className="r"><span>Circuit</span><b>Directeur → Owner, puis envoi PDF</b></div>
          </div>
          <div className="rec-modal-f" style={{ justifyContent: 'flex-end' }}>
            <button className="rec-ghost" disabled={busy || !valid} onClick={() => onSave(input(), false)}>Enregistrer le brouillon</button>
            <button className="rec-go" disabled={busy || !valid} onClick={() => onSave(input(), true)}>Soumettre à validation →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── réponse candidat ────────────────────────────────────────
export function OfferResponseModal({ app, busy, onClose, onConfirm }: {
  app: RecApplicationDTO;
  busy: boolean;
  onClose: () => void;
  onConfirm: (response: 'acceptee' | 'refusee' | 'contre', counter: string) => void;
}) {
  const o = recOfferOf(app)!;
  const [resp, setResp] = useState<'acceptee' | 'refusee' | 'contre' | null>(null);
  const [counter, setCounter] = useState('');
  const ok = resp != null && (resp !== 'contre' || counter.trim().length > 0);
  return (
    <div className="rec-modal-ov" onClick={onClose}>
      <div className="rec-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rec-modal-h">
          <h3>Réponse du candidat — {app.candidate.firstName} {app.candidate.lastName}</h3>
          <span className="rec-vchip">v{o.version}</span>
          <button className="rec-x" onClick={onClose}>✕</button>
        </div>
        <div className="rec-modal-b">
          <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--ink3)' }}>
            Offre envoyée{o.sentAt ? ` le ${recFmtDT(o.sentAt)}` : ''} — deadline {o.responseDeadline ? recFmtDate(o.responseDeadline) : '—'}.
          </p>
          <div className="rec-verdict" style={{ marginTop: 4 }}>
            <button className={`go${resp === 'acceptee' ? ' on' : ''}`} onClick={() => setResp('acceptee')}>🎉 Acceptée<br /><span>candidat recruté</span></button>
            <button className={`mid${resp === 'contre' ? ' on' : ''}`} onClick={() => setResp('contre')}>↩ Contre-proposition<br /><span>nouvelle version à construire</span></button>
            <button className={`stop${resp === 'refusee' ? ' on' : ''}`} onClick={() => setResp('refusee')}>✕ Refusée<br /><span>sortie rejet / vivier au choix</span></button>
          </div>
          {resp === 'contre' && (
            <div className="rec-field" style={{ marginTop: 12 }}><label>Détail de la contre-proposition (obligatoire)</label>
              <textarea rows={2} autoFocus placeholder="ex. Demande 260 000 DZD + télétravail 3 j/sem" value={counter} onChange={(e) => setCounter(e.target.value)}></textarea>
            </div>
          )}
          {resp === 'acceptee' && (
            <div className="rec-consent" style={{ marginTop: 12 }}>
              La candidature passe en <b>Recruté</b> — l’onboarding se crée automatiquement : dossier admin, plan J1 → J90, ressources et 1:1 du rôle. La demande sera pourvue au handoff (fiche employé).
            </div>
          )}
          <div className="rec-modal-f" style={{ justifyContent: 'flex-end' }}>
            <button className="rec-ghost" onClick={onClose}>Annuler</button>
            <button className="rec-go" disabled={!ok || busy} onClick={() => onConfirm(resp!, counter.trim())}>Enregistrer la réponse</button>
          </div>
        </div>
      </div>
    </div>
  );
}
